
import type {
  CreateMeetingRequest,
  CreateMeetingResponse,
  JoinMeetingRequest,
  JoinMeetingResponse,
  MeetingInfoResponse,
  ScreenShareRequest,
  ApiResponse,
} from '@/types';

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Meeting APIs
  async createMeeting(request: CreateMeetingRequest): Promise<CreateMeetingResponse> {
    const response = await this.request<CreateMeetingResponse['data']>('/api/meetings', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    if (!response.data) {
      throw new Error('API did not return meeting data');
    }
    return {
      ...response,
      data: response.data,
    } as CreateMeetingResponse;
  }

  async getMeeting(meetingId: string): Promise<MeetingInfoResponse> {
    const response = await this.request<MeetingInfoResponse['data']>(`/api/meetings/${meetingId}`);
    if (!response.data) {
      throw new Error('API did not return meeting info data');
    }
    return {
      ...response,
      data: response.data,
    } as MeetingInfoResponse;
  }

  async joinMeeting(
    meetingId: string,
    request: JoinMeetingRequest
  ): Promise<JoinMeetingResponse> {
    const response = await this.request<JoinMeetingResponse['data']>(`/api/meetings/${meetingId}/join`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    if (!response.data) {
      throw new Error('API did not return join meeting data');
    }
    return {
      ...response,
      data: response.data,
    } as JoinMeetingResponse;
  }

  async leaveMeeting(meetingId: string, participantId: string): Promise<ApiResponse> {
    return this.request(`/api/meetings/${meetingId}/leave`, {
      method: 'POST',
      body: JSON.stringify({ participantId }),
    });
  }

    /**
   * Upgrade meeting tier
   */
  async upgradeMeeting(
    meetingId: string,
    participantId: string,
    tier: 'pro' | 'enterprise'
  ): Promise<ApiResponse<{ tier: string; limits: any }>> {
    return this.request(`/api/meetings/${meetingId}/upgrade`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ participantId, tier }),
    });

  }

  // Screen Share APIs
  async toggleScreenShare(
    meetingId: string,
    request: ScreenShareRequest
  ): Promise<ApiResponse> {
    return this.request(`/api/meetings/${meetingId}/screen-share`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Participant APIs
  async getParticipants(meetingId: string): Promise<ApiResponse> {
    return this.request(`/api/meetings/${meetingId}/participants`);
  }

  async sendHeartbeat(meetingId: string, participantId: string): Promise<ApiResponse> {
    return this.request(`/api/meetings/${meetingId}/heartbeat`, {
      method: 'POST',
      body: JSON.stringify({ participantId }),
    });
  }

  async endMeeting(meetingId: string, participantId: string): Promise<ApiResponse> {
    return this.request(`/api/meetings/${meetingId}/end`, {
      method: 'POST',
      body: JSON.stringify({ participantId }),
    });
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    return this.request('/health');
  }

  
}

export const apiClient = new ApiClient();
